import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Check, ChevronRight, Plus, Search, Trash2, X } from 'lucide-react'
import {
  COURSES,
  COURSE_LABELS,
  FOOD_GROUPS,
  FOOD_GROUP_LABELS,
  MEAL_SLOTS,
  MEAL_SLOT_LABELS,
  UNITS,
  UNIT_LABELS,
  gramsOf,
  type Course,
  type FoodGroup,
  type Ingredient,
  type MealSlot,
  normaliseName,
  type Unit,
} from '@shared/events'
import { findFood, searchFoods } from '@shared/foods'
import { PLATE_BUCKET_LABELS, recipesById, type PlateBucket, type RecipePayload } from '@shared/plate'
import { Hoja } from '../components/Hoja'
import { Boton, Cabecera, Campo, Vacio } from '../components/ui'
import { COLOR_PORCION, porcionesDeReceta } from '../lib/platos'
import { useEvents } from '../lib/hooks'
import { useSession } from '../lib/session'
import { deleteEvent, newEvent, saveEvent } from '../lib/sync'

const PORCIONES: PlateBucket[] = ['vegetal', 'proteina', 'cereal']

export function Recetas() {
  const events = useEvents()
  const [editando, setEditando] = useState<{ id: string | null; recipe: RecipePayload } | null>(null)
  const [busqueda, setBusqueda] = useState('')

  const recetas = useMemo(
    () =>
      [...recipesById(events)]
        .map(([id, recipe]) => ({ id, recipe, porciones: porcionesDeReceta(recipe) }))
        .sort((a, b) => a.recipe.name.localeCompare(b.recipe.name, 'es')),
    [events],
  )
  const needle = normaliseName(busqueda)
  const filtradas = recetas.filter(({ recipe }) => !needle || normaliseName(recipe.name).includes(needle))

  return (
    <>
      <Cabecera
        titulo="Recetas"
        subtitulo={
          recetas.length === 0
            ? 'Cada receta alimenta el plan, la compra y el plato.'
            : `${recetas.length} ${recetas.length === 1 ? 'receta guardada' : 'recetas guardadas'}`
        }
      >
        <button
          type="button"
          aria-label="Nueva receta"
          onClick={() =>
            setEditando({ id: null, recipe: { name: '', ingredients: [], steps: '', slots: [], courses: [] } })
          }
          className="grid h-11 w-11 place-items-center rounded-full bg-primary text-primary-foreground outline-none transition-transform active:scale-95 focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plus className="h-5 w-5" strokeWidth={2.4} />
        </button>
      </Cabecera>

      {recetas.length === 0 ? (
        <div className="mt-5">
          <Vacio>
            El recetario está vacío. Cada receta que metas alimenta el plan, la compra y el plato.
          </Vacio>
        </div>
      ) : (
        <>
          <div className="relative mt-5">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              className="campo pl-10"
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
              placeholder="Buscar en el recetario…"
              aria-label="Buscar en el recetario"
            />
          </div>

          <ul className="mt-3 space-y-1.5">
            <AnimatePresence initial={false}>
              {filtradas.map(({ id, recipe, porciones }, index) => (
                <motion.li
                  key={id}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, delay: Math.min(index, 10) * 0.02 }}
                >
                  <button
                    type="button"
                    onClick={() => setEditando({ id, recipe })}
                    className="flex min-h-14 w-full items-center gap-3 rounded-2xl border border-border/70 bg-card px-4 py-2.5 text-left outline-none transition-colors hover:border-primary/40 focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{recipe.name}</span>
                      <span className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1" aria-hidden="true">
                          {PORCIONES.map((bucket) => (
                            <span
                              key={bucket}
                              title={PLATE_BUCKET_LABELS[bucket]}
                              className={`h-1.5 w-1.5 rounded-full ${
                                porciones.has(bucket) ? COLOR_PORCION[bucket] : 'bg-surface'
                              }`}
                            />
                          ))}
                        </span>
                        <span className="truncate">
                          {recipe.ingredients.length} ingrediente
                          {recipe.ingredients.length === 1 ? '' : 's'}
                          {recipe.slots.length > 0 &&
                            ` · ${recipe.slots.map((slot) => MEAL_SLOT_LABELS[slot].toLowerCase()).join(', ')}`}
                        </span>
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                </motion.li>
              ))}
            </AnimatePresence>
            {filtradas.length === 0 && (
              <li>
                <Vacio>No hay recetas con ese nombre.</Vacio>
              </li>
            )}
          </ul>
        </>
      )}

      <AnimatePresence>
        {editando && (
          <EditorReceta
            key="editor"
            id={editando.id}
            inicial={editando.recipe}
            onCerrar={() => setEditando(null)}
          />
        )}
      </AnimatePresence>
    </>
  )
}

function EditorReceta({
  id,
  inicial,
  onCerrar,
}: {
  id: string | null
  inicial: RecipePayload
  onCerrar: () => void
}) {
  const { user } = useSession()
  const [name, setName] = useState(inicial.name)
  const [steps, setSteps] = useState(inicial.steps)
  const [slots, setSlots] = useState<MealSlot[]>(inicial.slots)
  const [courses, setCourses] = useState<Course[]>(inicial.courses)
  const [ingredients, setIngredients] = useState<Ingredient[]>(inicial.ingredients)

  function actualizar(index: number, patch: Partial<Ingredient>): void {
    setIngredients((current) =>
      current.map((ingredient, i) => (i === index ? { ...ingredient, ...patch } : ingredient)),
    )
  }

  async function guardar(): Promise<void> {
    if (!user || !name.trim()) return
    await saveEvent(
      newEvent({
        type: 'recipe',
        createdBy: user.id,
        id: id ?? undefined,
        payload: {
          name: name.trim(),
          ingredients: ingredients.filter((ingredient) => ingredient.name.trim()),
          steps,
          slots,
          courses,
        },
      }),
    )
    onCerrar()
  }

  async function borrar(): Promise<void> {
    if (id) await deleteEvent(id)
    onCerrar()
  }

  const [confirmando, setConfirmando] = useState(false)

  return (
    <Hoja titulo={id ? 'Editar receta' : 'Nueva receta'} onCerrar={onCerrar}>
      <Campo id="receta-nombre" label="Nombre">
        <input
          id="receta-nombre"
          className="campo"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Lentejas de la abuela"
          autoFocus={!id}
        />
      </Campo>

      <Campo label="Cuándo encaja">
        <div className="flex flex-wrap gap-2">
          {MEAL_SLOTS.map((slot) => (
            <ChipMarcable
              key={slot}
              activo={slots.includes(slot)}
              onClick={() =>
                setSlots((current) =>
                  current.includes(slot) ? current.filter((value) => value !== slot) : [...current, slot],
                )
              }
            >
              {MEAL_SLOT_LABELS[slot]}
            </ChipMarcable>
          ))}
        </div>
      </Campo>

      <Campo label="Qué plato es">
        <div className="flex flex-wrap gap-2">
          {COURSES.map((course) => (
            <ChipMarcable
              key={course}
              activo={courses.includes(course)}
              onClick={() =>
                setCourses((current) =>
                  current.includes(course) ? current.filter((value) => value !== course) : [...current, course],
                )
              }
            >
              {COURSE_LABELS[course]}
            </ChipMarcable>
          ))}
        </div>
      </Campo>

      <h3 className="mt-5 font-display text-lg">Ingredientes</h3>
      <p className="mb-3 text-sm text-muted-foreground">
        Las cantidades son para el hogar entero, tal cual lo cocináis.
      </p>

      <AnimatePresence initial={false}>
        {ingredients.map((ingredient, index) => (
          <motion.div
            key={index}
            layout
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <FilaIngrediente
              ingredient={ingredient}
              onCambiar={(patch) => actualizar(index, patch)}
              onQuitar={() => setIngredients((current) => current.filter((_, i) => i !== index))}
            />
          </motion.div>
        ))}
      </AnimatePresence>

      <button
        type="button"
        className="mb-5 flex min-h-12 w-full items-center justify-center gap-1.5 rounded-2xl border border-dashed border-border text-sm font-medium text-muted-foreground outline-none transition-colors hover:border-primary/50 hover:bg-accent/60 hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() =>
          setIngredients((current) => [
            ...current,
            { name: '', quantity: null, unit: 'g', grams: null, group: null, fdcId: null, section: null },
          ])
        }
      >
        <Plus className="h-4 w-4" strokeWidth={2.4} />
        Añadir ingrediente
      </button>

      <Campo id="receta-pasos" label="Cómo se hace">
        <textarea
          id="receta-pasos"
          rows={4}
          className="campo h-auto py-2.5"
          value={steps}
          onChange={(event) => setSteps(event.target.value)}
        />
      </Campo>

      <Boton variante="principal" ancho disabled={!name.trim()} onClick={guardar}>
        Guardar
      </Boton>

      {id &&
        (confirmando ? (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Boton variante="peligro" onClick={borrar}>
              Sí, borrar
            </Boton>
            <Boton onClick={() => setConfirmando(false)}>No</Boton>
          </div>
        ) : (
          <Boton ancho className="mt-2" onClick={() => setConfirmando(true)}>
            <Trash2 className="h-4 w-4" />
            Borrar receta
          </Boton>
        ))}
    </Hoja>
  )
}

function ChipMarcable({
  activo,
  onClick,
  children,
}: {
  activo: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onClick}
      className={`label-caps inline-flex min-h-10 items-center gap-1 rounded-full border px-3.5 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
        activo
          ? 'border-sage bg-filled text-sage-deep'
          : 'border-border bg-background text-muted-foreground hover:border-primary/40'
      }`}
    >
      {activo && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
      {children}
    </button>
  )
}

/**
 * Una línea de ingrediente.
 *
 * Al escribir el nombre, el catálogo propone el alimento y rellena su grupo y
 * el peso de una unidad: nadie debería tener que decidir si el atún es carne
 * o pescado para que el plato salga bien.
 */
function FilaIngrediente({
  ingredient,
  onCambiar,
  onQuitar,
}: {
  ingredient: Ingredient
  onCambiar: (patch: Partial<Ingredient>) => void
  onQuitar: () => void
}) {
  const [sugerencias, setSugerencias] = useState<ReturnType<typeof searchFoods>>([])

  function escribirNombre(value: string): void {
    onCambiar({ name: value })
    const conocido = findFood(value)
    setSugerencias(conocido ? [] : searchFoods(value, 5))
    if (conocido) aplicarAlimento(conocido.name, conocido.group, conocido.gramsPerUnit)
  }

  function aplicarAlimento(name: string, group: FoodGroup, gramsPerUnit?: number): void {
    const grams =
      ingredient.unit === 'ud' && ingredient.quantity !== null && gramsPerUnit
        ? Math.round(gramsPerUnit * ingredient.quantity)
        : null
    onCambiar({ name, group, grams })
    setSugerencias([])
  }

  function cambiarCantidad(raw: string): void {
    const value = raw.trim() ? Number(raw.replace(',', '.')) : null
    const quantity = value !== null && Number.isFinite(value) ? value : null
    const food = findFood(ingredient.name)
    const grams =
      ingredient.unit === 'ud' && quantity !== null && food?.gramsPerUnit
        ? Math.round(food.gramsPerUnit * quantity)
        : null
    onCambiar({ quantity, grams })
  }

  function cambiarUnidad(unit: Unit): void {
    const food = findFood(ingredient.name)
    const grams =
      unit === 'ud' && ingredient.quantity !== null && food?.gramsPerUnit
        ? Math.round(food.gramsPerUnit * ingredient.quantity)
        : null
    onCambiar({ unit, grams })
  }

  const peso = gramsOf(ingredient)

  return (
    <div className="mb-2.5 rounded-2xl border border-border/70 bg-background/60 p-2.5">
      <div className="grid grid-cols-[1fr_4.5rem_4.75rem] gap-2">
        <input
          className="campo bg-card"
          value={ingredient.name}
          placeholder="Ingrediente"
          aria-label="Ingrediente"
          onChange={(event) => escribirNombre(event.target.value)}
        />
        <input
          className="campo bg-card px-2.5"
          inputMode="decimal"
          value={ingredient.quantity ?? ''}
          placeholder="Cant."
          aria-label="Cantidad"
          onChange={(event) => cambiarCantidad(event.target.value)}
        />
        <select
          className="campo bg-card px-2"
          value={ingredient.unit}
          aria-label="Unidad"
          onChange={(event) => cambiarUnidad(event.target.value as Unit)}
        >
          {UNITS.map((unit) => (
            <option key={unit} value={unit}>
              {UNIT_LABELS[unit]}
            </option>
          ))}
        </select>
      </div>

      {sugerencias.length > 0 && (
        <ul className="mt-1.5 overflow-hidden rounded-2xl border border-border bg-card">
          {sugerencias.map((food) => (
            <li key={food.name} className="border-b border-border/70 last:border-b-0">
              <button
                type="button"
                className="flex min-h-11 w-full items-center gap-2 px-3 text-left text-sm outline-none hover:bg-accent/60 focus-visible:bg-accent/60"
                onClick={() => aplicarAlimento(food.name, food.group, food.gramsPerUnit)}
              >
                {food.name}
                <span className="text-muted-foreground">· {FOOD_GROUP_LABELS[food.group]}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2 flex items-center gap-2">
        <select
          className="campo h-10 min-w-0 flex-1 bg-card text-sm"
          value={ingredient.group ?? ''}
          aria-label="Grupo de alimento"
          onChange={(event) => onCambiar({ group: (event.target.value || null) as FoodGroup | null })}
        >
          <option value="">Sin grupo</option>
          {FOOD_GROUPS.map((group) => (
            <option key={group} value={group}>
              {FOOD_GROUP_LABELS[group]}
            </option>
          ))}
        </select>
        <span
          className={`shrink-0 text-xs tabular-nums ${peso === null ? 'text-primary' : 'text-muted-foreground'}`}
        >
          {peso === null ? 'sin peso' : `${peso} g`}
        </span>
        <button
          type="button"
          aria-label={`Quitar ${ingredient.name || 'ingrediente'}`}
          onClick={onQuitar}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
